import { useState, useCallback } from 'react';
import debounce from 'react-utils/debounce';
import { useQuery } from 'data-fetching-client';
import { H5, H4 } from 'UIComponents/elements/headings';
import UILoadingSpinner from 'UIComponents/loading/UILoadingSpinner';
import UIErrorMessage from 'UIComponents/error/UIErrorMessage';
import UIPanelSection from 'UIComponents/panel/UIPanelSection';
import FormattedMessage from 'I18n/components/FormattedMessage';
import FormattedMessageWithObjectTypeName from 'automation-ui-i18n/FormattedMessageWithObjectTypeName';
import { GET_OBJECT_TYPE_BY_ID } from 'automation-ui-enrollment/queries/ObjectTypesQueries';
import { GET_ALL_OBJECT_PROPERTIES_BY_OBJECT } from 'automation-ui-enrollment/queries/ObjectPropertiesQueries';
import { hasRowConfiguration } from 'automation-ui-enrollment/components/enrollment/manual/ObjectTableMetadata';
import PreviewEnrollTable from 'automation-ui-enrollment/components/enrollment/manual/PreviewEnrollTable';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const TABLE_WIDTH = 750;
const SEARCH_DEBOUNCE_MS = 250;
const SCROLL_DEBOUNCE_MS = 300;
const ManualEnrollmentIndividual = ({
  objectTypeId,
  handleObjectSelection,
  selectedObjects,
  scrollContainer,
  mode,
  isMultiSelect = true
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState(searchQuery);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedSetSearchQuery = useCallback(debounce(setDebouncedSearchQuery, SEARCH_DEBOUNCE_MS), []);
  const {
    data,
    error,
    fetchMore
  } = useQuery(GET_ALL_OBJECT_PROPERTIES_BY_OBJECT, {
    variables: {
      objectTypeId,
      query: debouncedSearchQuery,
      count: 50,
      offset: 0
    }
  });
  const debouncedFetchMore = debounce(fetchMore, SCROLL_DEBOUNCE_MS);
  const {
    loading: objectTypeLoading,
    data: objectTypeResponse,
    error: objectTypeError
  } = useQuery(GET_OBJECT_TYPE_BY_ID, {
    variables: {
      objectTypeId
    }
  });
  const objectTypeDefinition = objectTypeResponse === null || objectTypeResponse === void 0 ? void 0 : objectTypeResponse.enrollment_objectTypeById;
  const handleSearchQueryChange = ({
    target: {
      value
    }
  }) => {
    setSearchQuery(value);
    debouncedSetSearchQuery(value);
  };
  const handleAddOrRemoveObjects = ({
    objectId,
    shouldEnroll
  }) => {
    if (isMultiSelect) {
      const setOfSelectedObjects = new Set(selectedObjects || []);
      if (shouldEnroll) {
        setOfSelectedObjects.add(objectId);
      } else {
        setOfSelectedObjects.delete(objectId);
      }
      handleObjectSelection([...setOfSelectedObjects]);
    } else {
      handleObjectSelection([objectId]);
    }
  };
  const setOfSelectedObjects = new Set(selectedObjects);
  if (objectTypeLoading && !hasRowConfiguration(objectTypeId)) {
    return /*#__PURE__*/_jsx(UILoadingSpinner, {
      minHeight: 150,
      layout: "centered"
    });
  }
  if (objectTypeError && !hasRowConfiguration(objectTypeId)) {
    return /*#__PURE__*/_jsx(UIErrorMessage, {
      title: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "automation-ui-enrollment.redesign.enrollExistingPanel.enrollFromList.table.error"
      }),
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "automation-ui-enrollment.redesign.enrollExistingPanel.enrollFromList.table.errorDetail"
      })
    });
  }
  if (error) {
    return /*#__PURE__*/_jsx(UIErrorMessage, {
      title: /*#__PURE__*/_jsx(H4, {
        children: /*#__PURE__*/_jsx(FormattedMessageWithObjectTypeName, {
          baseLangKey: "automation-ui-enrollment.redesign.enrollExistingPanel.enrollIndividual.error_cob",
          objectTypeId: objectTypeId
        })
      }),
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "automation-ui-enrollment.redesign.enrollExistingPanel.enrollIndividual.errorDetail"
      })
    });
  }
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsxs(UIPanelSection, {
      className: "p-top-0 m-bottom-0",
      children: [/*#__PURE__*/_jsx(H5, {
        className: "m-bottom-2 m-top-10",
        children: /*#__PURE__*/_jsx(FormattedMessageWithObjectTypeName, {
          baseLangKey: "automation-ui-enrollment.redesign.enrollExistingPanel.enrollIndividual.title_cob",
          objectTypeId: objectTypeId
        })
      }), mode === 'EXISTING' ? /*#__PURE__*/_jsx(FormattedMessageWithObjectTypeName, {
        baseLangKey: "automation-ui-enrollment.redesign.enrollExistingPanel.enrollIndividual.informationExisting_cob",
        objectTypeId: objectTypeId
      }) : /*#__PURE__*/_jsx(FormattedMessageWithObjectTypeName, {
        baseLangKey: "automation-ui-enrollment.redesign.enrollExistingPanel.selected.information_cob",
        objectTypeId: objectTypeId
      })]
    }), /*#__PURE__*/_jsx(PreviewEnrollTable, {
      objectTypeId: objectTypeId,
      allObjectProperties: data && data.allPropertiesByObject,
      tableWidth: TABLE_WIDTH,
      selectable: true,
      isMultiSelect: isMultiSelect,
      checkedItems: setOfSelectedObjects,
      onSelectObject: handleAddOrRemoveObjects,
      onLoadMore: debouncedFetchMore,
      objectTypeDefinition: objectTypeDefinition,
      scrollContainer: scrollContainer,
      searchQuery: searchQuery,
      handleSearchQueryChange: handleSearchQueryChange
    })]
  });
};
export default ManualEnrollmentIndividual;